/**
 * Everything Tank Royale sends to phones, as pure functions of plain values.
 * DOM-free and free of runtime imports so a test can load it under plain Node.
 */
import type { ControllerSpec } from '@forgesworn/joystick-host';

export const GAME_TITLE = 'TANK ROYALE';
export const SPEC_PREFIX = 'TANK';
export const MAX_PLAYERS = 4;
export const FALLBACK_COLOUR = '#b48cff';
/** Arcade initials: exactly three capital letters. */
export const INITIALS_RE = /^[A-Z]{3}$/;
export const PHASES = ['WAITING', 'STARTING', 'ROUND LIVE', 'BETWEEN ROUNDS'] as const;
export const PAUSE_MESSAGE = 'GAME PAUSED';
export const RESUME_MESSAGE = 'GAME RESUMED';
export const ROUND_WIN_LABEL = 'tank round win';

export type RoundKind = 'lobby' | 'countdown' | 'playing' | 'over';
export interface LeaderRow { p: number; label: string; score: number; colour: string; tournamentTotal?: number }
export interface SpectatorViewData { title: string; leaderboard: LeaderRow[]; highlight?: string }

/** The name as stored from phone context: initials or nothing. */
export function acceptName(name: unknown): string | undefined {
  return typeof name === 'string' && INITIALS_RE.test(name) ? name : undefined;
}

export function cheerLabelFor(slot: number): string { return `P${slot + 1}`; }

export function nameFor(tank: { p: number; ai: boolean }, name?: string): string {
  if (tank.ai) return `P${tank.p + 1} · BOT`;
  return name ? `P${tank.p + 1} · ${name}` : `P${tank.p + 1}`;
}

export function spectatorSpec(slot: number, colour: string): ControllerSpec {
  return {
    name: `${SPEC_PREFIX} · SPECTATOR ${slot - MAX_PLAYERS + 1}`,
    version: 1,
    slots: {
      A: { icon: '👏', label: 'CHEER', colour },
    },
  };
}

/** The addressed player spec: listing profile with the team colour applied. */
export function playerSpec(base: ControllerSpec, p: number, colour: string): ControllerSpec {
  return {
    ...base,
    name: `${SPEC_PREFIX} · P${p + 1}`,
    theme: { ...base.theme, accent: colour },
    slots: { ...base.slots, A: { ...base.slots.A, colour } },
  };
}

export function phaseFor(kind: RoundKind): (typeof PHASES)[number] {
  return kind === 'over' ? 'BETWEEN ROUNDS'
    : kind === 'countdown' ? 'STARTING'
    : kind === 'playing' ? 'ROUND LIVE'
    : 'WAITING';
}

export function pauseMessage(paused: boolean): { type: 'controller-feedback'; message: string; locked: boolean } {
  return { type: 'controller-feedback', message: paused ? PAUSE_MESSAGE : RESUME_MESSAGE, locked: paused };
}

export function roundWinLabel(): string { return ROUND_WIN_LABEL; }

export interface SpectatorInput {
  tanks: ReadonlyArray<{ p: number; ai: boolean; score: number; colour: string }>;
  roundKind: RoundKind;
  winnerP: number | null;
  names: ReadonlyMap<number, string>;
  tournamentTotals: ReadonlyMap<number, number>;
  /** Slot of the most recent live cheer, or null. */
  lastCheerSlot: number | null;
}

export function buildSpectatorView(input: SpectatorInput): SpectatorViewData {
  const tanks = [...input.tanks].sort((a, b) => b.score - a.score);
  const phase = phaseFor(input.roundKind);
  const winnerName = input.roundKind === 'over' && input.winnerP !== null
    ? (() => {
        const winner = input.tanks.find((t) => t.p === input.winnerP);
        return winner ? nameFor(winner, input.names.get(winner.p)) : `P${input.winnerP! + 1}`;
      })()
    : null;
  return {
    title: `${GAME_TITLE} · ${phase}`,
    leaderboard: tanks.slice(0, 4).map((tank) => ({
      p: tank.p,
      label: nameFor(tank, input.names.get(tank.p)),
      score: tank.score,
      colour: tank.colour,
      ...(input.tournamentTotals.has(tank.p) ? { tournamentTotal: input.tournamentTotals.get(tank.p)! } : {}),
    })),
    ...(input.lastCheerSlot !== null
      ? { highlight: `${cheerLabelFor(input.lastCheerSlot)} cheered!` }
      : (winnerName ? { highlight: `${winnerName} took the round` } : {})),
  };
}

const SAMPLE_COLOURS = ['#58ff58', '#ff8a3a', '#b48cff', '#ffd84a'];

/** Everything the game can emit, for a validator test. `base` is the listing profile. */
export function enumerateControllerStrings(base?: ControllerSpec): {
  specs: ControllerSpec[]; feedback: string[]; scoreLabels: string[];
  spectatorViews: { title: string; leaderboard: LeaderRow[]; highlight?: string }[];
} {
  const specs: ControllerSpec[] = [];
  const fallback: ControllerSpec = base ?? { name: SPEC_PREFIX, version: 1, slots: { A: { icon: '●', label: 'FIRE' } } } as ControllerSpec;
  if (base) specs.push(base);
  for (let p = 0; p < MAX_PLAYERS; p++) specs.push(playerSpec(fallback, p, SAMPLE_COLOURS[p % 4]!));
  for (let slot = MAX_PLAYERS; slot < MAX_PLAYERS + 8; slot++) specs.push(spectatorSpec(slot, SAMPLE_COLOURS[slot % 4]!));
  const feedback = [PAUSE_MESSAGE, RESUME_MESSAGE];
  const scoreLabels = [roundWinLabel()];
  const spectatorViews: SpectatorViewData[] = [];
  const kinds: RoundKind[] = ['lobby', 'countdown', 'playing', 'over'];
  const names = new Map<number, string>([[1, 'ABC'], [3, 'ZZZ']]);
  for (const roundKind of kinds) {
    for (const withTotals of [false, true]) {
      for (const cheer of [null, 5, 11]) {
        for (const winnerP of [null, 0, 1, 2, 7]) {
          const tanks = [0, 1, 2, 3].map((p) => ({ p, ai: p % 2 === 0, score: p * 50, colour: SAMPLE_COLOURS[p]! }));
          const totals = new Map<number, number>(withTotals ? [[0, 0], [1, 200], [3, 35]] : []);
          spectatorViews.push(buildSpectatorView({ tanks, roundKind, winnerP, names, tournamentTotals: totals, lastCheerSlot: cheer }));
        }
      }
    }
  }
  return { specs, feedback, scoreLabels, spectatorViews };
}
